import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";

import { addEMP } from "../../../services/clearing/emps";
import useToastActionWrapper from "../../../hooks/useToastActionWrapper";
import CountryDropdown from "../../../components/inputs/CountryDropdown";
import LanguageDropdown, {
  LANGUGAGE_OPTIONS,
} from "../../../components/inputs/LanguageDropdown";
import { ISO_COUNTRY_OPTIONS } from "../../../components/inputs/consts";
import TextInputField from "../../../components/inputs/TextInputField";
import { EMPValidationSchema } from "../validation";

const initialValues: Record<string, any> = {
  mandantName: null,
  actualEmps: 1,
  iban: null,
  bic: null,
  billingAddressAdditional: null,
  billingAddressCity: null,
  billingAddressCompanyName: null,
  billingAddressCountry: "DE",
  billingAddressHouseNumber: null,
  billingAddressPostcode: null,
  billingAddressStreet: null,
  businessAddressAdditional: null,
  businessAddressCity: null,
  businessAddressCompanyName: null,
  businessAddressCountry: "DE",
  businessAddressHouseNumber: null,
  businessAddressPostcode: null,
  businessAddressStreet: null,
  exemptFromElectricityTax: false,
  exemptFromVat: false,
  email: null,
  firstName: null,
  invoiceEmail: null,
  invoiceLanguage: "DE",
  lastName: null,
  notes: null,
  phone: null,
  taxId: null,
  vatId: null,
};

const AddEMP = () => {
  const toast = useRef(null);
  const { toastActionWrapper, isLoading } = useToastActionWrapper(toast);
  const [empDetails, setEMPDetails] = useState(initialValues);
  const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});

  const [showBillingAddressFields, setShowBillingAddressFields] =
    useState(false);

  const validateField = async (fieldName, value) => {
    try {
      await EMPValidationSchema.validateAt(fieldName, { [fieldName]: value });
      setValidationErrors((prev) => ({ ...prev, [fieldName]: null }));
    } catch (error) {
      setValidationErrors((prev) => ({ ...prev, [fieldName]: error.message }));
    }
  };

  const handleChange = (e, field) => {
    const value = e?.target?.value ? e?.target?.value : e?.value;

    validateField(field, value);
    setEMPDetails((prev) => {
      return { ...prev, [field]: value };
    });
  };

  const handleCheckboxChange = (e, field) => {
    setEMPDetails((prev) => ({ ...prev, [field]: e?.checked || false }));
  };

  const handleSubmit = async () => {
    const payload = !showBillingAddressFields
      ? reuseBillingAdressAsBusiness(empDetails)
      : empDetails;

    try {
      await EMPValidationSchema.validate(payload);

      await toastActionWrapper({
        action: () => addEMP(payload),
        successMessage: "EMP added successfully.",
        errorMessage: "Failed to add emp.",
      });

      if (!showBillingAddressFields) setEMPDetails(payload);
    } catch (err) {
      toast.current.show({
        severity: "error",
        summary: "Invalid form data",
        detail: "Fill in all required fields!",
        life: 3000,
      });
    }
  };

  return (
    <div className="p-4">
      <Toast ref={toast} className="z-10" />
      <Card className="p-4" title="Add New EMP">
        <div className="pt-2 p-fluid grid formgrid">
          <TextInputField
            name="mandantName"
            label="Mandant Name"
            required
            value={empDetails?.mandantName}
            onChange={(e) => handleChange(e, "mandantName")}
            errorMessage={validationErrors?.mandantName}
          />

          <div className="field col-12 md:col-6">
            <label htmlFor="actualEmps" className="input-label">
              Actual EMPs *
            </label>
            <InputNumber
              id="actualEmps"
              value={empDetails?.actualEmps}
              onValueChange={(e) => handleChange(e, "actualEmps")}
              className="input-field"
              min={1}
              required
            />
          </div>

          <TextInputField
            name="firstName"
            label="First Name"
            value={empDetails?.firstName}
            onChange={(e) => handleChange(e, "firstName")}
            errorMessage={validationErrors?.firstName}
          />

          <TextInputField
            name="lastName"
            label="Last Name"
            value={empDetails?.lastName}
            onChange={(e) => handleChange(e, "lastName")}
            errorMessage={validationErrors?.lastName}
          />

          <TextInputField
            name="email"
            label="Email"
            required
            value={empDetails?.email || null}
            onChange={(e) => handleChange(e, "email")}
            errorMessage={validationErrors?.email}
          />

          <TextInputField
            name="invoiceEmail"
            label="Invoice Email"
            required
            value={empDetails?.invoiceEmail}
            onChange={(e) => handleChange(e, "invoiceEmail")}
            errorMessage={validationErrors?.invoiceEmail}
          />

          <TextInputField
            name="phone"
            label="Phone"
            value={empDetails?.phone}
            onChange={(e) => handleChange(e, "phone")}
            errorMessage={validationErrors?.phone}
          />

          <TextInputField
            name="iban"
            label="IBAN"
            required
            value={empDetails?.iban}
            onChange={(e) => handleChange(e, "iban")}
            errorMessage={validationErrors?.iban}
          />

          <TextInputField
            name="bic"
            label="BIC"
            value={empDetails?.bic}
            onChange={(e) => handleChange(e, "bic")}
            errorMessage={validationErrors?.bic}
          />

          <TextInputField
            name="taxId"
            label="Tax ID"
            required
            value={empDetails?.taxId}
            onChange={(e) => handleChange(e, "taxId")}
            errorMessage={validationErrors?.taxId}
          />

          <TextInputField
            name="vatId"
            label="VAT ID"
            value={empDetails?.vatId}
            onChange={(e) => handleChange(e, "vatId")}
            errorMessage={validationErrors?.vatId}
          />

          <div className="field col-12 md:col-6">
            <label className="input-label">Invoice Language *</label>
            <LanguageDropdown
              value={
                LANGUGAGE_OPTIONS.find(
                  (option) => option.code === empDetails?.invoiceLanguage
                ) || null
              }
              onChange={(value) =>
                handleChange({ value: value?.code || "DE" }, "invoiceLanguage")
              }
              placeholder="Select a language"
              className="input-field"
            />
          </div>

          <TextInputField
            name="notes"
            label="Notes"
            value={empDetails?.notes}
            onChange={(e) => handleChange(e, "notes")}
            errorMessage={validationErrors?.notes}
            containerClassName="field col-12"
          />

          {/* Business Address Section */}
          <div className="field col-12">
            <h3 className="mb-2">Business Address</h3>
          </div>
          <TextInputField
            name="businessAddressCompanyName"
            label="Company Name"
            required
            value={empDetails?.businessAddressCompanyName}
            onChange={(e) => handleChange(e, "businessAddressCompanyName")}
            errorMessage={validationErrors?.businessAddressCompanyName}
          />
          <TextInputField
            name="businessAddressStreet"
            label="Street"
            required
            value={empDetails?.businessAddressStreet}
            onChange={(e) => handleChange(e, "businessAddressStreet")}
            errorMessage={validationErrors?.businessAddressStreet}
          />
          <TextInputField
            name="businessAddressHouseNumber"
            label="House Number"
            value={empDetails?.businessAddressHouseNumber}
            onChange={(e) => handleChange(e, "businessAddressHouseNumber")}
            errorMessage={validationErrors?.businessAddressHouseNumber}
            containerClassName="field col-12 md:col-4"
          />
          <TextInputField
            name="businessAddressPostcode"
            label="Postal Code"
            required
            value={empDetails?.businessAddressPostcode}
            onChange={(e) => handleChange(e, "businessAddressPostcode")}
            errorMessage={validationErrors?.businessAddressPostcode}
            containerClassName="field col-12 md:col-4"
          />
          <TextInputField
            name="businessAddressCity"
            label="City"
            required
            value={empDetails?.businessAddressCity}
            onChange={(e) => handleChange(e, "businessAddressCity")}
            errorMessage={validationErrors?.businessAddressCity}
            containerClassName="field col-12 md:col-4"
          />
          <div className="field col-12 md:col-6">
            <label className="input-label">Country *</label>
            <CountryDropdown
              value={
                ISO_COUNTRY_OPTIONS.find(
                  (option) => option.code === empDetails?.businessAddressCountry
                ) || null
              }
              onChange={(value) =>
                handleChange(
                  { value: value?.code || null },
                  "businessAddressCountry"
                )
              }
              className="input-field"
            />
          </div>
          <TextInputField
            name="businessAddressAdditional"
            label="Additional Address Info"
            value={empDetails?.businessAddressAdditional}
            onChange={(e) => handleChange(e, "businessAddressAdditional")}
            errorMessage={validationErrors?.businessAddressAdditional}
          />

          {/* Billing Address Section */}
          <div className="field col-12 pb-3 flex flex-row align-items-center justify-content-start">
            <Checkbox
              checked={showBillingAddressFields}
              onChange={() => setShowBillingAddressFields((p) => !p)}
            />
            <label className="input-label m-2">
              Billing Address (Optional)
            </label>
          </div>

          {showBillingAddressFields && (
            <>
              <div className="field col-12">
                <h3 className="mb-2">Billing Address</h3>
              </div>
              <TextInputField
                name="billingAddressCompanyName"
                label="Company Name"
                value={empDetails?.billingAddressCompanyName}
                onChange={(e) => handleChange(e, "billingAddressCompanyName")}
                errorMessage={validationErrors?.billingAddressCompanyName}
              />
              <TextInputField
                name="billingAddressStreet"
                label="Street"
                value={empDetails?.billingAddressStreet}
                onChange={(e) => handleChange(e, "billingAddressStreet")}
                errorMessage={validationErrors?.billingAddressStreet}
              />
              <TextInputField
                name="billingAddressHouseNumber"
                label="House Number"
                value={empDetails?.billingAddressHouseNumber}
                onChange={(e) => handleChange(e, "billingAddressHouseNumber")}
                errorMessage={validationErrors?.billingAddressHouseNumber}
                containerClassName="field col-12 md:col-4"
              />
              <TextInputField
                name="billingAddressPostcode"
                label="Postal Code"
                value={empDetails?.billingAddressPostcode}
                onChange={(e) => handleChange(e, "billingAddressPostcode")}
                errorMessage={validationErrors?.billingAddressPostcode}
                containerClassName="field col-12 md:col-4"
              />
              <TextInputField
                name="billingAddressCity"
                label="City"
                value={empDetails?.billingAddressCity}
                onChange={(e) => handleChange(e, "billingAddressCity")}
                errorMessage={validationErrors?.billingAddressCity}
                containerClassName="field col-12 md:col-4"
              />
              <div className="field col-12 md:col-6">
                <label className="input-label">Country</label>
                <CountryDropdown
                  value={
                    ISO_COUNTRY_OPTIONS.find(
                      (option) =>
                        option.code === empDetails?.billingAddressCountry
                    ) || null
                  }
                  onChange={(value) =>
                    handleChange(
                      { value: value?.code || null },
                      "billingAddressCountry"
                    )
                  }
                  className="input-field"
                />
              </div>
              <TextInputField
                name="billingAddressAdditional"
                label="Additional Address Info"
                value={empDetails?.billingAddressAdditional}
                onChange={(e) => handleChange(e, "billingAddressAdditional")}
                errorMessage={validationErrors?.billingAddressAdditional}
              />
            </>
          )}

          {/* Tax Exemptions */}
          <div className="field col-12 md:col-6 flex flex-row align-items-center justify-content-start">
            <Checkbox
              inputId="exemptFromElectricityTax"
              checked={empDetails?.exemptFromElectricityTax}
              onChange={(e) =>
                handleCheckboxChange(e, "exemptFromElectricityTax")
              }
            />
            <label className="input-label m-2">
              Exempt from Electricity Tax
            </label>
          </div>
          <div className="field col-12 md:col-6 flex flex-row align-items-center justify-content-start">
            <Checkbox
              inputId="exemptFromVat"
              checked={empDetails?.exemptFromVat}
              onChange={(e) => handleCheckboxChange(e, "exemptFromVat")}
            />
            <label className="input-label m-2">Exempt from VAT</label>
          </div>

          <div className="field flex flex-column col-12 md:flex-row md:justify-content-end gap-2">
            <Button
              label="Submit"
              onClick={handleSubmit}
              className="p-button w-full md:w-20rem"
              disabled={isLoading}
              loading={isLoading}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

const reuseBillingAdressAsBusiness = (data) => ({
  ...data,
  billingAddressCompanyName: data.businessAddressCompanyName,
  billingAddressStreet: data.businessAddressStreet,
  billingAddressHouseNumber: data.businessAddressHouseNumber,
  billingAddressPostcode: data.businessAddressPostcode,
  billingAddressCity: data.businessAddressCity,
  billingAddressCountry: data.businessAddressCountry,
  billingAddressAdditional: data.businessAddressAdditional,
});

export default AddEMP;
